import React from 'react';

import { Button, Spinner, Text } from '@change/ds-core-ui';
import { ArrowRightIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/homepage/homepage.yml';

import {
	HeroTypingOverlay,
	PromptInput,
	PromptInputActions,
	PromptInputTextarea,
} from '#src/app/pages/homepage/shared/components';
import { useHeroTypingPlaceholder } from '#src/app/pages/homepage/shared/hooks';

import { FormHelper } from './components/FormHelper';
import { LinkChip } from './components/LinkChip';
import { useSocialPostUrlForm } from './hooks/useSocialPostUrlForm';

type SubmitAreaProps = Readonly<{ hasChip: boolean; isSubmitting: boolean; submitAriaLabel: string }>;

/**
 * One button for both states so the swap animates from the arrow pill to the full-width label. The
 * label stays mounted, collapsed and aria-hidden, so the width transition has a stable child.
 */
const SubmitArea: React.FC<SubmitAreaProps> = ({ hasChip, isSubmitting, submitAriaLabel }) => (
	<PromptInputActions data-qa="prompt-input-actions" className="static mx-3 mt-3 mb-3 justify-end">
		<Button
			aria-label={hasChip ? undefined : submitAriaLabel}
			aria-live="polite"
			// the spinner already takes the leading slot
			trailingIcon={isSubmitting ? undefined : <ArrowRightIcon />}
			loading={isSubmitting}
			variant="primary"
			type="submit"
			size="small"
			data-qa="prompt-input-actions-button"
			className={cn(
				// Fixed height keeps the collapsed pill a circle. `motion-safe:` and `!` let these win over
				// the button's own transition classes; background-color stays for its hover.
				'bg-fill-interactive motion-safe:duration-t4! h-12 rounded-full py-0 motion-safe:transition-[width,gap,padding,background-color] motion-safe:ease-[cubic-bezier(0.2,1.06,0.6,1)]!',
				hasChip ? 'w-full' : 'w-12 gap-0 px-0',
			)}
		>
			<span
				aria-hidden={!hasChip}
				className={cn(
					// max-width grows with the pill so the text never overflows it; only the fade is delayed
					'flex min-w-0 items-center overflow-hidden whitespace-nowrap',
					'motion-safe:duration-t4 motion-safe:transition-[max-width,opacity] motion-safe:ease-[cubic-bezier(0.2,1.06,0.6,1)]',
					hasChip ? 'max-w-[24rem] opacity-100 motion-safe:[transition-delay:0s,150ms]' : 'max-w-0 opacity-0',
				)}
			>
				<Translate value="corgi.homepage.hero_section.input_prompt_submit_button_generate" />
			</span>
		</Button>
	</PromptInputActions>
);

const SocialPostUrlFormComponent: React.FC = () => {
	const { translate } = useI18n();
	const {
		data: { isSubmitting, isResolvingPreview, error, chip, multiSource },
		actions: { onSubmit, onPaste, onInputChange, onRemoveChip },
	} = useSocialPostUrlForm();
	const {
		data: { value: prompt, placeholder, showOverlay, descriptionId },
		actions: { onValueChange: setPrompt },
	} = useHeroTypingPlaceholder();

	const submitAriaLabel = isSubmitting
		? translate('corgi.homepage.hero_section.input_prompt_submit_button_loading')
		: translate('corgi.homepage.hero_section.input_prompt_submit_button');

	let promptContent: React.ReactNode;
	if (chip) {
		promptContent = <LinkChip {...chip} onRemove={onRemoveChip} data-qa="social-post-chip" />;
	} else {
		promptContent = (
			// fill the box so a click anywhere above the button focuses the textarea
			<div className="flex w-full grow flex-col">
				<PromptInputTextarea
					maxLength={500}
					name="prompt_or_url"
					placeholder={placeholder}
					aria-labelledby="social-post-input-heading"
					aria-describedby={showOverlay ? descriptionId : undefined}
					enterKeyHint="send"
					onPaste={onPaste}
					onChange={onInputChange}
					data-qa="social-post-prompt-textarea"
					className="grow pb-2"
				/>
				{showOverlay && <HeroTypingOverlay descriptionId={descriptionId} className="right-5" />}
				{isResolvingPreview && (
					<div className="flex items-center gap-2 px-5 pt-1 pb-2" data-qa="social-post-resolving">
						<Spinner className="size-4" />
						<Text variant="body" size="small" className="text-typography-weak">
							{multiSource ? (
								<Translate value="corgi.homepage.hero_section.input_resolving_link_label_multi_source" />
							) : (
								<Translate value="corgi.homepage.hero_section.input_resolving_link_label" />
							)}
						</Text>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="mb-3 flex w-full flex-col items-center justify-center lg:mb-3">
			<div className="flex w-full max-w-[500px] flex-col gap-4">
				<div>
					<Text
						className="text-center"
						variant="body"
						size="large"
						fontWeight="bold"
						as="h2"
						id="social-post-input-heading"
					>
						<Translate value="corgi.homepage.hero_section.input_prompt_label_alt" />
					</Text>
				</div>
				<div className="relative">
					<form onSubmit={onSubmit} data-qa="social-post-url-form" className="relative z-10">
						{/* min-h fits the tallest chip state so the box doesn't jump when the chip replaces the
						    textarea: a three-line TikTok title (~190px), or a two-line title plus description (~206px) */}
						<PromptInput
							data-qa="social-post-prompt"
							className={cn(
								'group elevation-low flex-col justify-between',
								multiSource ? 'min-h-[212px]' : 'min-h-[196px]',
							)}
							value={prompt}
							onValueChange={setPrompt}
						>
							{promptContent}
							<SubmitArea hasChip={!!chip} isSubmitting={isSubmitting} submitAriaLabel={submitAriaLabel} />
						</PromptInput>
					</form>

					<FormHelper error={error} multiSource={multiSource} />
				</div>
			</div>
		</div>
	);
};

export const SocialPostUrlForm = withI18nPlaceholders(I18N_PLACEHOLDERS, SocialPostUrlFormComponent);
